import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  MessageSquare,
  Send,
  Users,
  Loader2,
  Bot,
} from 'lucide-react';
import { formatTime } from '../lib/utils';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const ChatPage = () => {
  const { api, user, isDirector } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchRooms();
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      fetchMessages(selectedRoom.id);
    }
  }, [selectedRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchRooms = async () => {
    try {
      const response = await api().get('/chat/rooms');
      setRooms(response.data);
      // Auto-select first room
      if (response.data.length > 0 && !selectedRoom) {
        setSelectedRoom(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch chat rooms:', error);
      toast.error('Failed to load chat rooms');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (roomId) => {
    setLoadingMessages(true);
    try {
      const response = await api().get(`/chat/rooms/${roomId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedRoom) return;

    setSending(true);
    try {
      const response = await api().post(`/chat/rooms/${selectedRoom.id}/messages`, {
        room_id: selectedRoom.id,
        content: newMessage.trim(),
      });
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getRoomIcon = (room) => {
    if (room.type === 'group') return <Users className="h-5 w-5" />;
    if (room.type === 'ai_assistant') return <Bot className="h-5 w-5" />;
    return <MessageSquare className="h-5 w-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-180px)] md:h-[calc(100vh-140px)] pb-16 md:pb-0">
      <div className="flex h-full gap-4">
        {/* Room List - Hidden on mobile when room is selected */}
        <Card className={cn(
          'border-zinc-100 w-full md:w-80 flex-shrink-0',
          selectedRoom && 'hidden md:flex md:flex-col'
        )}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Chats</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="space-y-1">
              {rooms.length === 0 ? (
                <p className="text-center text-zinc-500 py-4">No chat rooms available</p>
              ) : (
                rooms.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => setSelectedRoom(room)}
                    className={cn(
                      'w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors',
                      selectedRoom?.id === room.id
                        ? 'bg-zinc-900 text-white'
                        : 'hover:bg-zinc-100 text-zinc-700'
                    )}
                    data-testid={`chat-room-${room.id}`}
                  >
                    <div className={cn(
                      'h-10 w-10 rounded-full flex items-center justify-center',
                      selectedRoom?.id === room.id ? 'bg-white/20' : 'bg-zinc-100'
                    )}>
                      {getRoomIcon(room)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{room.room_name}</p>
                      <p className={cn(
                        'text-sm truncate',
                        selectedRoom?.id === room.id ? 'text-white/70' : 'text-zinc-500'
                      )}>
                        {room.type === 'group' ? 'Company Chat' : 
                         room.type === 'ai_assistant' ? 'AI Assistant' : 'Direct Message'}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Chat Area */}
        {selectedRoom ? (
          <Card className="border-zinc-100 flex-1 flex flex-col">
            {/* Chat Header */}
            <CardHeader className="pb-2 border-b border-zinc-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                  onClick={() => setSelectedRoom(null)}
                >
                  ←
                </Button>
                <div className="h-10 w-10 rounded-full bg-zinc-100 flex items-center justify-center">
                  {getRoomIcon(selectedRoom)}
                </div>
                <div>
                  <CardTitle className="text-lg">{selectedRoom.room_name}</CardTitle>
                  <p className="text-sm text-zinc-500">
                    {selectedRoom.type === 'group' ? 'All team members' :
                     selectedRoom.type === 'ai_assistant' ? 'Your AI helper' : 'Private conversation'}
                  </p>
                </div>
              </div>
            </CardHeader>

            {/* Messages */}
            <CardContent className="flex-1 p-0 overflow-hidden">
              <ScrollArea className="h-full p-4">
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-zinc-500">
                    <MessageSquare className="h-12 w-12 mb-2" />
                    <p>No messages yet</p>
                    <p className="text-sm">Start the conversation!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message) => {
                      const isOwnMessage = message.sender_id === user?.id;
                      const isAI = message.is_ai_response;
                      
                      return (
                        <div
                          key={message.id}
                          className={cn(
                            'flex',
                            isOwnMessage ? 'justify-end' : 'justify-start'
                          )}
                        >
                          <div className={cn(
                            'max-w-[80%] rounded-2xl px-4 py-2',
                            isOwnMessage 
                              ? 'bg-zinc-900 text-white rounded-br-md'
                              : isAI
                                ? 'bg-amber-50 border border-amber-200 rounded-bl-md'
                                : 'bg-zinc-100 text-zinc-900 rounded-bl-md'
                          )}>
                            {!isOwnMessage && (
                              <p className={cn(
                                'text-xs font-medium mb-1',
                                isAI ? 'text-amber-600' : 'text-zinc-500'
                              )}>
                                {message.sender_name}
                              </p>
                            )}
                            <p className="whitespace-pre-wrap">{message.content}</p>
                            <p className={cn(
                              'text-xs mt-1',
                              isOwnMessage ? 'text-white/60' : 'text-zinc-400'
                            )}>
                              {formatTime(message.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
            </CardContent>

            {/* Message Input */}
            <div className="p-4 border-t border-zinc-100 flex-shrink-0">
              <form onSubmit={sendMessage} className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  disabled={sending}
                  className="flex-1"
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-zinc-900 hover:bg-zinc-800"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </form>
            </div>
          </Card>
        ) : (
          <Card className="border-zinc-100 flex-1 hidden md:flex items-center justify-center">
            <div className="text-center text-zinc-500">
              <MessageSquare className="h-16 w-16 mx-auto mb-4" />
              <p className="text-lg font-medium">Select a chat</p>
              <p className="text-sm">Choose a conversation from the list</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
